import { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Container, Stack, Title, Text, Paper, Button, Group, Alert, Accordion, ThemeIcon, ActionIcon, Skeleton, Divider } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconInfoCircle, IconUsers, IconSalad, IconChefHat, IconShoppingCart, IconWorld, IconMeat, IconPalette, IconChevronLeft, IconLock, IconSettings, IconBrandPinterest, IconCalendar, IconToolsKitchen2 } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { useFeatureFlag } from '../hooks/useFeatureFlags';
import { useAuth } from '../hooks/useAuth';
import { FamilyProfile, effectiveCookingStyle } from '../types/familyProfile';
import {
  FamilySizePreference,
  CookingPreference,
  CookingStylePreference,
  DietaryPreference,
  DietaryPreferenceRef,
  CuisinePreference,
  CuisinePreferenceRef,
  FlavorPreference,
  GroceryPreference,
  ProductPreferenceRef,
  ProteinPreference,
  ProteinPreferenceRef,
  PrivacyPreference,
  GoogleCalendarPreference,
  ContactEmailPreference,
  NameAndPhotoPreference,
} from '../components/Profile';
import { FloatingContactButton } from '../components/Contact';
import { initiatePinterestConnection, disconnectPinterest } from '../utils/pinterestAuth';
import {
  DEFAULT_ADULTS,
  DEFAULT_COOKING_SKILL,
  DEFAULT_KIDS,
  DEFAULT_PROTEIN_DIVERSITY,
  defaultIncludeLeftovers,
} from '../utils/familyProfileDefaults';

// JSON.stringify with sorted object keys, and sorted primitive arrays, so
// comparisons don't produce false positives when the same data is assembled
// in a different order. The profile's string arrays (allergies, proteins,
// cooking methods, ...) are semantically sets: toggling a selected item off
// and back on re-appends it at the end, which must compare equal.
const stableStringify = (value: unknown): string =>
  JSON.stringify(value, (_key, val) => {
    if (Array.isArray(val)) {
      return val.every(item => typeof item !== 'object' || item === null)
        ? [...val].sort()
        : val;
    }
    if (val && typeof val === 'object') {
      return Object.keys(val as Record<string, unknown>)
        .sort()
        .reduce((acc: Record<string, unknown>, k) => {
          acc[k] = (val as Record<string, unknown>)[k];
          return acc;
        }, {});
    }
    return val;
  });

// Snapshot of everything the Save button persists. Connection status flags are
// excluded: connect/disconnect flows persist themselves server-side, so they
// should not surface the "unsaved changes" pill.
const serializeProfileState = (profile: FamilyProfile | null, profilePublic: boolean): string => {
  if (!profile) return '';
  const { krogerConnected, pinterestConnected, googleCalendarConnected, ...tracked } = profile;
  return stableStringify({
    profile: {
      ...tracked,
      // cookingStyle is stored sparse (fields absent until touched), so
      // compare effective values: toggling a control away from its default
      // and back materializes the default, which must not read as dirty.
      cookingStyle: effectiveCookingStyle(tracked.cookingStyle),
    },
    profilePublic,
  });
};

export const ProfilePage = () => {
  usePageTitle('Preferences | Meals You Love');
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState<FamilyProfile | null>(null);
  const [profilePublic, setProfilePublic] = useState<boolean>(false);
  const additionalMealsEnabled = useFeatureFlag('additional_meals');
  const [contactEmailInfo, setContactEmailInfo] = useState<{
    contactEmail?: string;
    pendingContactEmail?: string;
  }>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);

  const hasUnsavedChanges = useMemo(
    () => savedSnapshot !== null && serializeProfileState(profile, profilePublic) !== savedSnapshot,
    [profile, profilePublic, savedSnapshot]
  );

  // Refs for flushing text inputs
  const dietaryRef = useRef<DietaryPreferenceRef>(null);
  const cuisineRef = useRef<CuisinePreferenceRef>(null);
  const proteinRef = useRef<ProteinPreferenceRef>(null);
  const productRef = useRef<ProductPreferenceRef>(null);

  // Refs for accordion items (for scrolling)
  const accordionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Ref to store the latest profile state
  const profileRef = useRef<FamilyProfile | null>(profile);

  // Keep profileRef in sync with profile state
  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  // Check for section query parameter
  const searchParams = new URLSearchParams(location.search);
  const section = searchParams.get('section');
  const googleCalendarParam = searchParams.get('google_calendar');
  const errorParam = searchParams.get('error');
  const isGoogleCalendarRedirect = googleCalendarParam === 'connected' ||
    errorParam === 'google_calendar_auth_failed' ||
    errorParam === 'google_calendar_no_refresh_token';

  const [accordionValue, setAccordionValue] = useState<string | null>(
    isGoogleCalendarRedirect ? 'google-calendar' :
    section === 'privacy' || section === 'contact-email' ? 'account-settings' : null
  );

  // Scroll to the Google Calendar section after mount when redirected from OAuth
  useEffect(() => {
    if (isGoogleCalendarRedirect && accordionRefs.current['google-calendar']) {
      const element = accordionRefs.current['google-calendar'];
      if (element) {
        setTimeout(() => {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }, 600);
      }
    }
  }, [isGoogleCalendarRedirect, loading]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleAccordionChange = (value: string | null) => {
    const previousValue = accordionValue;
    setAccordionValue(value);

    if (!value) return;
    const target = accordionRefs.current[value];
    if (!target) return;

    const scrollToTarget = () => {
      const headerOffset = 80;
      const offsetPosition =
        target.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    };

    // If a sibling accordion above the target is currently collapsing, its
    // height transition will shift the target's position. Wait for that
    // panel's transitionend before scrolling so we land at the final layout.
    const previousElement = previousValue
      ? accordionRefs.current[previousValue]
      : null;
    const previousIsAbove =
      previousElement &&
      previousElement.getBoundingClientRect().top <
        target.getBoundingClientRect().top;

    if (previousIsAbove && previousElement) {
      const panel = previousElement.querySelector<HTMLElement>('[role="region"]');
      if (panel) {
        const onTransitionEnd = (event: TransitionEvent) => {
          // Accordion panels animate height; ignore unrelated properties.
          if (event.propertyName !== 'height') return;
          panel.removeEventListener('transitionend', onTransitionEnd);
          scrollToTarget();
        };
        panel.addEventListener('transitionend', onTransitionEnd);
        return;
      }
    }

    // No layout shift expected — scroll on the next frame so React can commit
    // the open state first.
    requestAnimationFrame(scrollToTarget);
  };

  // Load profile data
  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;

      try {
        const token = await user.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const familyProfile = data.user?.familyProfile;

          // Set profilePublic (defaults to false if not set)
          const isPublic = data.user?.profilePublic ?? false;
          setProfilePublic(isPublic);

          // Self-persisting (like the connection flags), so it stays out of
          // the unsaved-changes snapshot.
          setContactEmailInfo({
            contactEmail: data.user?.contactEmail,
            pendingContactEmail: data.user?.pendingContactEmail,
          });

          let loadedProfile: FamilyProfile;
          if (familyProfile) {
            const adults = familyProfile.adults || DEFAULT_ADULTS;
            const kids = familyProfile.kids || DEFAULT_KIDS;
            loadedProfile = {
              adults,
              kids,
              dietaryRestrictions: familyProfile.dietaryRestrictions || [],
              allergies: familyProfile.allergies || [],
              cuisinePreferences: familyProfile.cuisinePreferences || [],
              avoidCuisines: familyProfile.avoidCuisines || [],
              // Mirror CuisinePreference's mount-time default so it lands in
              // the saved-state snapshot instead of registering as an edit.
              cuisineAdventure: familyProfile.cuisineAdventure ??
                ((familyProfile.cuisinePreferences || []).length > 0 ? 50 : undefined),
              cookingSkill: familyProfile.cookingSkill || DEFAULT_COOKING_SKILL,
              dislikes: familyProfile.dislikes || [],
              favoriteProteins: familyProfile.favoriteProteins || [],
              avoidProteins: familyProfile.avoidProteins || [],
              proteinDiversity: familyProfile.proteinDiversity || DEFAULT_PROTEIN_DIVERSITY,
              flavorProfile: familyProfile.flavorProfile || {
                flavorIntensity: 'moderate',
                preferences: '',
              },
              includeLeftovers: familyProfile.includeLeftovers ?? defaultIncludeLeftovers(adults, kids),
              preferredCookingMethods: familyProfile.preferredCookingMethods || [],
              cookingStyle: familyProfile.cookingStyle,
              krogerConnected: familyProfile.krogerConnected || false,
              krogerInfo: familyProfile.krogerInfo,
              productPreferences: familyProfile.productPreferences,
              pinterestConnected: familyProfile.pinterestConnected || false,
              googleCalendarConnected: familyProfile.googleCalendarConnected || false,
              calendarPreferences: familyProfile.calendarPreferences,
            };
          } else {
            // Default profile if none exists
            loadedProfile = {
              adults: DEFAULT_ADULTS,
              kids: DEFAULT_KIDS,
              dietaryRestrictions: [],
              allergies: [],
              cuisinePreferences: [],
              avoidCuisines: [],
              cookingSkill: DEFAULT_COOKING_SKILL,
              dislikes: [],
              favoriteProteins: [],
              avoidProteins: [],
              proteinDiversity: DEFAULT_PROTEIN_DIVERSITY,
              flavorProfile: {
                flavorIntensity: 'moderate',
                preferences: '',
              },
              includeLeftovers: defaultIncludeLeftovers(DEFAULT_ADULTS, DEFAULT_KIDS),
              preferredCookingMethods: [],
              krogerConnected: false,
              krogerInfo: undefined,
              productPreferences: undefined,
            };
          }
          setProfile(loadedProfile);
          setSavedSnapshot(serializeProfileState(loadedProfile, isPublic));
        } else {
          setError('Failed to load your profile. Please try again.');
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        setError('Failed to load your profile. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const saveProfile = async () => {
    if (!user || !profileRef.current) return;

    setSaving(true);
    setError(null);

    // Flush all text inputs before saving
    dietaryRef.current?.flushInputs();
    cuisineRef.current?.flushInputs();
    proteinRef.current?.flushInputs();
    productRef.current?.flushInputs();

    // Wait for React to batch and complete state updates
    await new Promise(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });

    try {
      const token = await user.getIdToken();

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/family-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          familyProfile: {
            ...profileRef.current,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          profilePublic: profilePublic
        }),
      });

      if (response.ok) {
        setSavedSnapshot(serializeProfileState(profileRef.current, profilePublic));
        notifications.show({
          title: 'Profile updated',
          message: 'Your preferences have been saved successfully!',
          color: 'green',
          icon: <IconCheck size={18} />,
          autoClose: 4000,
        });
      } else {
        const data = await response.json();
        const message = data.error || 'Failed to save profile. Please try again.';
        setError(message);
        notifications.show({
          title: 'Save failed',
          message,
          color: 'red',
          icon: <IconAlertCircle size={18} />,
        });
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to save profile. Please try again.');
      notifications.show({
        title: 'Save failed',
        message: 'Failed to save profile. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
      });
    } finally {
      setSaving(false);
    }
  };

  const updateProfile = (updates: Partial<FamilyProfile>) => {
    setProfile(prev => {
      if (!prev) return null;
      const newProfile = { ...prev, ...updates };
      if (updates.flavorProfile) {
        newProfile.flavorProfile = {
          ...prev.flavorProfile,
          ...updates.flavorProfile,
        };
      }
      if (updates.krogerInfo) {
        newProfile.krogerInfo = {
          ...prev.krogerInfo,
          ...updates.krogerInfo,
        };
      }
      return newProfile;
    });
  };

  if (loading) {
    return (
      <Container size="md" py="xl">
        <Stack gap="xl">
          {/* Header skeleton */}
          <Stack gap="lg" align="center">
            <Skeleton height={80} width={80} circle />
            <Stack gap="sm" align="center">
              <Skeleton height={32} width={180} />
              <Skeleton height={22} width="min(340px, 85%)" />
            </Stack>
          </Stack>

          {/* Accordion items skeleton */}
          <Stack gap="md" w={{ base: '100%', sm: 700 }} mx="auto">
            {Array.from({ length: 10 }).map((_, i) => (
              <Paper key={i} p="md" radius="lg" withBorder>
                <Group gap="md" align="center" wrap="nowrap">
                  <Skeleton height={32} width={32} radius="md" />
                  <Skeleton height={20} width={`${30 + (i % 3) * 10}%`} />
                </Group>
              </Paper>
            ))}
          </Stack>

        </Stack>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container size="md" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" radius="lg">
          Failed to load profile data. Please refresh the page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Stack gap="md" ta="center" align="center" pos="relative">
          <ActionIcon 
            variant="subtle" 
            onClick={handleBack}
            size="lg"
            pos="absolute"
            top={0}
            left={0}
            style={{ zIndex: 1 }}
          >
            <IconChevronLeft size={20} />
          </ActionIcon>
          <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
            <IconSettings size={40} />
          </ThemeIcon>
          <Stack gap="xs" align="center">
            <Title order={1} size="h1" fw={700} c="dark">
              Preferences
            </Title>
            <Text size="lg" c="dimmed" ta="center" maw={500}>
              Customize your preferences and privacy settings
            </Text>
          </Stack>
        </Stack>

        {/* Error Alert */}
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" radius="lg">
            {error}
          </Alert>
        )}


        {/* Profile Sections */}
        <Accordion
          variant="separated"
          radius="lg"
          w={{ base: '100%', sm: 700 }}
          mx="auto"
          value={accordionValue}
          onChange={handleAccordionChange}
        >
          <Accordion.Item value="account-settings" ref={(el) => { accordionRefs.current['account-settings'] = el; }}>
            <Accordion.Control>
              <Group gap="md" align="center" wrap="nowrap">
                <ThemeIcon size={32} radius="md" color="orange" variant="light">
                  <IconLock size={18} />
                </ThemeIcon>
                <Text fw={600} size="lg">Account</Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="lg">
                <NameAndPhotoPreference />
                <Divider />
                <PrivacyPreference
                  value={{ profilePublic }}
                  onChange={(value) => setProfilePublic(value.profilePublic)}
                />
                <Divider />
                <ContactEmailPreference
                  authEmail={user?.email}
                  initialContactEmail={contactEmailInfo.contactEmail}
                  initialPendingContactEmail={contactEmailInfo.pendingContactEmail}
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="family-size" ref={(el) => { accordionRefs.current['family-size'] = el; }}>
            <Accordion.Control>
              <Group gap="md" align="center" wrap="nowrap">
                <ThemeIcon size={32} radius="md" color="orange" variant="light">
                  <IconUsers size={18} />
                </ThemeIcon>
                <Text fw={600} size="lg">Family size</Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <FamilySizePreference
                value={{ adults: profile.adults, kids: profile.kids }}
                onChange={(value) => updateProfile({ adults: value.adults, kids: value.kids })}
              />
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="dietary-restrictions" ref={(el) => { accordionRefs.current['dietary-restrictions'] = el; }}>
            <Accordion.Control>
              <Group gap="md" align="center" wrap="nowrap">
                <ThemeIcon size={32} radius="md" color="orange" variant="light">
                  <IconSalad size={18} />
                </ThemeIcon>
                <Text fw={600} size="lg">Dietary preferences</Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <DietaryPreference
                ref={dietaryRef}
                value={{ dietaryRestrictions: profile.dietaryRestrictions, allergies: profile.allergies }}
                onChange={(value) => updateProfile({ dietaryRestrictions: value.dietaryRestrictions, allergies: value.allergies })}
              />
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="cooking-preferences" ref={(el) => { accordionRefs.current['cooking-preferences'] = el; }}>
            <Accordion.Control>
              <Group gap="md" align="center" wrap="nowrap">
                <ThemeIcon size={32} radius="md" color="orange" variant="light">
                  <IconChefHat size={18} />
                </ThemeIcon>
                <Text fw={600} size="lg">Cooking preferences</Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <CookingPreference
                value={{
                  cookingSkill: profile.cookingSkill,
                  includeLeftovers: profile.includeLeftovers,
                  preferredCookingMethods: profile.preferredCookingMethods
                }}
                onChange={(value) => updateProfile({
                  cookingSkill: value.cookingSkill,
                  includeLeftovers: value.includeLeftovers,
                  preferredCookingMethods: value.preferredCookingMethods
                })}
              />
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="cuisine-preferences" ref={(el) => { accordionRefs.current['cuisine-preferences'] = el; }}>
            <Accordion.Control>
              <Group gap="md" align="center" wrap="nowrap">
                <ThemeIcon size={32} radius="md" color="orange" variant="light">
                  <IconWorld size={18} />
                </ThemeIcon>
                <Text fw={600} size="lg">Cuisine preferences</Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <CuisinePreference
                ref={cuisineRef}
                value={{
                  cuisinePreferences: profile.cuisinePreferences,
                  avoidCuisines: profile.avoidCuisines,
                  dislikes: profile.dislikes,
                  cuisineAdventure: profile.cuisineAdventure
                }}
                onChange={(value) => updateProfile({
                  cuisinePreferences: value.cuisinePreferences,
                  avoidCuisines: value.avoidCuisines,
                  dislikes: value.dislikes,
                  cuisineAdventure: value.cuisineAdventure
                })}
              />
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="protein-preferences" ref={(el) => { accordionRefs.current['protein-preferences'] = el; }}>
            <Accordion.Control>
              <Group gap="md" align="center" wrap="nowrap">
                <ThemeIcon size={32} radius="md" color="orange" variant="light">
                  <IconMeat size={18} />
                </ThemeIcon>
                <Text fw={600} size="lg">Protein preferences</Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <ProteinPreference
                ref={proteinRef}
                value={{
                  favoriteProteins: profile.favoriteProteins,
                  avoidProteins: profile.avoidProteins,
                  proteinDiversity: profile.proteinDiversity
                }}
                onChange={(value) => updateProfile({
                  favoriteProteins: value.favoriteProteins,
                  avoidProteins: value.avoidProteins,
                  proteinDiversity: value.proteinDiversity
                })}
              />
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="flavor-profile" ref={(el) => { accordionRefs.current['flavor-profile'] = el; }}>
            <Accordion.Control>
              <Group gap="md" align="center" wrap="nowrap">
                <ThemeIcon size={32} radius="md" color="orange" variant="light">
                  <IconPalette size={18} />
                </ThemeIcon>
                <Text fw={600} size="lg">Flavor profile</Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <FlavorPreference
                value={{ flavorProfile: profile.flavorProfile! }}
                onChange={(value) => updateProfile({ flavorProfile: value.flavorProfile })}
              />
            </Accordion.Panel>
          </Accordion.Item>

          {additionalMealsEnabled && (
            <Accordion.Item value="planning-defaults" ref={(el) => { accordionRefs.current['planning-defaults'] = el; }}>
              <Accordion.Control>
                <Group gap="md" align="center" wrap="nowrap">
                  <ThemeIcon size={32} radius="md" color="orange" variant="light">
                    <IconToolsKitchen2 size={18} />
                  </ThemeIcon>
                  <Text fw={600} size="lg">Meal planning defaults</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <CookingStylePreference
                  value={{ cookingStyle: profile.cookingStyle }}
                  onChange={(value) => updateProfile({ cookingStyle: value.cookingStyle })}
                />
              </Accordion.Panel>
            </Accordion.Item>
          )}

          <Accordion.Item value="grocery-connection" ref={(el) => { accordionRefs.current['grocery-connection'] = el; }}>
            <Accordion.Control>
              <Group gap="md" align="center" wrap="nowrap">
                <ThemeIcon size={32} radius="md" color="orange" variant="light">
                  <IconShoppingCart size={18} />
                </ThemeIcon>
                <Text fw={600} size="lg">Kroger connection</Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <GroceryPreference
                ref={productRef}
                value={{
                  krogerConnected: profile.krogerConnected,
                  krogerInfo: profile.krogerInfo,
                  productPreferences: profile.productPreferences,
                }}
                onChange={(value) => updateProfile({
                  krogerConnected: value.krogerConnected,
                  krogerInfo: value.krogerInfo,
                  productPreferences: value.productPreferences,
                })}
              />
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="pinterest-connection" ref={(el) => { accordionRefs.current['pinterest-connection'] = el; }}>
            <Accordion.Control>
              <Group gap="md" align="center" wrap="nowrap">
                <ThemeIcon size={32} radius="md" color="orange" variant="light">
                  <IconBrandPinterest size={18} />
                </ThemeIcon>
                <Text fw={600} size="lg">Pinterest connection</Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                {profile?.pinterestConnected ? (
                  <>
                    <Alert icon={<IconCheck size={16} />} color="green" variant="light" radius="lg">
                      Your Pinterest account is connected! You can import recipes from your boards in the recipe import modal.
                    </Alert>
                    <Button
                      variant="subtle"
                      color="red"
                      size="sm"
                      style={{ alignSelf: 'center' }}
                      onClick={async () => {
                        if (!user) return;
                        try {
                          await disconnectPinterest(user);
                          setProfile(prev => prev ? { ...prev, pinterestConnected: false } : prev);
                          notifications.show({
                            title: 'Pinterest disconnected',
                            message: 'Your Pinterest account has been disconnected.',
                            color: 'blue',
                            icon: <IconCheck size={18} />,
                          });
                        } catch {
                          notifications.show({
                            title: 'Error',
                            message: 'Failed to disconnect Pinterest. Please try again.',
                            color: 'red',
                            icon: <IconAlertCircle size={18} />,
                          });
                        }
                      }}
                    >
                      Disconnect Pinterest
                    </Button>
                  </>
                ) : (
                  <>
                    <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light" radius="lg">
                      Connect your Pinterest account to browse your boards and import saved recipes directly into your cookbook.
                    </Alert>
                    <Button
                      color="red"
                      size="sm"
                      w={{ base: '100%', sm: '220px' }}
                      style={{ alignSelf: 'center' }}
                      onClick={async () => {
                        if (!user) return;
                        try {
                          await initiatePinterestConnection(user);
                        } catch {
                          notifications.show({
                            title: 'Error',
                            message: 'Failed to connect to Pinterest. Please try again.',
                            color: 'red',
                            icon: <IconAlertCircle size={18} />,
                          });
                        }
                      }}
                    >
                      Connect Pinterest
                    </Button>
                  </>
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item value="google-calendar" ref={(el) => { accordionRefs.current['google-calendar'] = el; }}>
            <Accordion.Control>
              <Group gap="md" align="center" wrap="nowrap">
                <ThemeIcon size={32} radius="md" color="orange" variant="light" style={{ flexShrink: 0 }}>
                  <IconCalendar size={18} />
                </ThemeIcon>
                <Text fw={600} size="lg">Google Calendar connection</Text>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <GoogleCalendarPreference
                value={{
                  googleCalendarConnected: profile.googleCalendarConnected,
                  calendarPreferences: profile.calendarPreferences,
                }}
                onChange={(val) => updateProfile(val)}
              />
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

      </Stack>

      {/* Floating unsaved-changes pill. The contact FAB is hidden while this
          is visible, so the pill can be truly centered. */}
      <Box
        style={{
          position: 'fixed',
          bottom: '24px',
          left: '16px',
          right: '16px',
          zIndex: 200,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Paper
          radius="xl"
          px="lg"
          withBorder
          style={{
            // Match the contact FAB's shadow
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            pointerEvents: hasUnsavedChanges ? 'auto' : 'none',
            opacity: hasUnsavedChanges ? 1 : 0,
            transform: hasUnsavedChanges ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.2s ease, transform 0.2s ease',
          }}
          aria-hidden={!hasUnsavedChanges}
        >
          <Group gap="md" wrap="nowrap">
            <Text size="sm" fw={500}>
              Unsaved changes
            </Text>
            <Button
              size="sm"
              radius="xl"
              color="orange"
              variant="filled"
              onClick={saveProfile}
              loading={saving}
            >
              Save
            </Button>
          </Group>
        </Paper>
      </Box>
      <FloatingContactButton hidden={hasUnsavedChanges} />
    </Container>
  );
};
